import { FormFieldInputType } from 'models/enum';
import { ContentItem } from 'models/ui';

export enum SectionTitle {
  INFORMED_CONSENT = 'Informed Consent',
  GENERAL_INFO = 'General Info'
}

export enum SaveType {
  SAVE_CONTINUE = 'saveContinue'
}

export enum DropdownAction {
  CLEAR_LABEL = 'Clear',
  CLEAR_OPTION_ID = '__clear__'
}

export const TEXT_AREA_MAX_LENGTH = 150;
export const PLACEHOLDER_OPTION_ID = '__placeholder__';

export const buildPlaceholderItem = (placeholder: string): ContentItem =>
  ({
    id: PLACEHOLDER_OPTION_ID,
    name: placeholder,
    label: placeholder,
    value: '',
    selected: false,
    isActive: true,
    isHidden: false,
    hidden: true,
    disabled: true,
    displayOrder: -1,
    onChange: () => undefined
  } as unknown as ContentItem);

export const buildClearItem = (): ContentItem =>
  ({
    id: DropdownAction.CLEAR_OPTION_ID,
    name: DropdownAction.CLEAR_LABEL,
    label: DropdownAction.CLEAR_LABEL,
    value: '',
    selected: false,
    isActive: true,
    isHidden: false,
    displayOrder: 0,
    onChange: () => undefined
  } as unknown as ContentItem);

export enum FormField {
  PROCEDURE_DESCRIPTION = 'procedureDescription',
  CONDITION_DIAGNOSIS = 'conditionDiagnosis',
  PURPOSE = 'purpose',
  MATERIAL_RISK_A = 'materialRiskA',
  MATERIAL_RISK_B = 'materialRiskB',
  MATERIAL_RISK_C = 'materialRiskC',
  MATERIAL_RISK_D = 'materialRiskD',
  LIKELIHOOD_OF_SUCCESS = 'likelihoodOfSuccess',
  PROGNOSIS_IF_DECLINED = 'prognosisIfDeclined',
  PRACTICAL_ALTERNATIVES = 'practicalAlternatives',
  INTERPRETER_NAMES = 'interpreterNames',
  INTERPRETER_MODALITY = 'interpreterModality',
  LANGUAGE = 'language',
  DISABILITY_ACCOMMODATIONS = 'disabilityAccommodations',
  DISABILITY_OTHER = 'disabilityOther',
  PATIENT_NAME = 'patientName',
  PATIENT_SIGNATURE = 'patientSignature',
  WITNESS_SIGNATURE = 'witnessSignature',
  PHYSICIAN_SIGNATURE = 'physicianSignature',
  GUARDIAN_NAME = 'guardianName',
  ADDITIONAL_COMMENTS = 'additionalComments'
}

export const REQUIRED_FORM_FIELDS = new Set<FormField>([
  FormField.PATIENT_NAME,
  FormField.PATIENT_SIGNATURE,
  FormField.WITNESS_SIGNATURE,
  FormField.PHYSICIAN_SIGNATURE,
  FormField.GUARDIAN_NAME
]);

export const PROVIDER_NURSE_FIELD = 'providerNurse';
export const PROVIDER_NURSE_LABEL = 'Provider / Nurse';

export const STATIC_ACK_FIELD = {
  ITEM_5: 'staticAckItem5',
  ITEM_9: 'staticAckItem9',
  ITEM_10: 'staticAckItem10',
  ITEM_11: 'staticAckItem11',
  ITEM_12: 'staticAckItem12',
  ITEM_13: 'staticAckItem13',
  ITEM_14: 'staticAckItem14',
  ITEM_15: 'staticAckItem15',
  ITEM_16: 'staticAckItem16'
};

export enum FormFieldLabel {
  PROCEDURE_DESCRIPTION = 'I acknowledge and understand that the following procedure(s) that has been described in general terms, is to be performed on me:',
  CONDITION_DIAGNOSIS = 'I understand that my condition has been diagnosed as:',
  PURPOSE = 'I understand that the purpose of this procedure is:',
  MATERIAL_RISK_A = 'a.',
  MATERIAL_RISK_B = 'b.',
  MATERIAL_RISK_C = 'c.',
  MATERIAL_RISK_D = 'd.',
  LIKELIHOOD_OF_SUCCESS = 'I understand that the likelihood of success of this procedure is:',
  PROGNOSIS_IF_DECLINED = 'I understand that if I do not undergo this proposed procedure, my prognosis is:',
  PRACTICAL_ALTERNATIVES = 'I understand that the practical alternatives to this procedure recognized and accepted by reasonable physicians include:',
  INTERPRETER_NAMES = 'Interpreter Name/ID',
  INTERPRETER_MODALITY = 'Interpreter Modality',
  LANGUAGE = 'Language',
  DISABILITY_ACCOMMODATIONS = 'Disability Accommodations',
  DISABILITY_OTHER = 'Other',
  PATIENT_NAME = 'Patient Name',
  PATIENT_SIGNATURE = 'Patient/Legal Guardian Signature',
  WITNESS_SIGNATURE = 'Witness Signature',
  PHYSICIAN_SIGNATURE = 'Physician Signature',
  GUARDIAN_NAME = 'Guardian Name',
  ADDITIONAL_COMMENTS = 'Additional materials, if any, used during the informed consent process for this procedure:'
}

export enum Placeholder {
  PROCEDURE_DESCRIPTION = 'Enter the procedure(s)',
  CONDITION_DIAGNOSIS = 'Enter the details',
  PURPOSE = 'Enter the purpose',
  MATERIAL_RISK = 'Enter risk',
  LIKELIHOOD_OF_SUCCESS = 'Select',
  PROGNOSIS_IF_DECLINED = 'Enter the prognosis',
  PRACTICAL_ALTERNATIVES = 'Enter the alternatives',
  INTERPRETER_NAMES = 'Enter Name/ID',
  INTERPRETER_MODALITY = 'Select',
  LANGUAGE = 'Select language',
  DISABILITY = 'Select',
  ENTER_NAME = 'Enter name',
  ADDITIONAL_COMMENTS = 'Enter the additional materials'
}

export enum LikelihoodOfSuccess {
  EXCELLENT = 'Excellent',
  GOOD = 'Good',
  FAIR = 'Fair',
  POOR = 'Poor',
  UNKNOWN = 'Unknown'
}

export enum Language {
  SPANISH = 'Spanish',
  MANDARIN = 'Chinese (Mandarin)',
  CANTONESE = 'Chinese (Cantonese)',
  TAGALOG = 'Tagalog',
  VIETNAMESE = 'Vietnamese',
  ARABIC = 'Arabic',
  FRENCH = 'French',
  KOREAN = 'Korean',
  RUSSIAN = 'Russian',
  PORTUGUESE = 'Portuguese',
  HAITIAN_CREOLE = 'Haitian Creole',
  OTHER = 'Other'
}

export const LIKELIHOOD_OF_SUCCESS_OPTIONS = Object.values(LikelihoodOfSuccess).map((value) => ({ name: value, value }));
export const LANGUAGE_OPTIONS = Object.values(Language).map((value) => ({ name: value, value }));
export const DISABILITY_OTHER_VALUE = 'disabilityOther';

export const FORM_FIELD_LABEL_MAP: Record<FormField, string> = {
  [FormField.PROCEDURE_DESCRIPTION]: FormFieldLabel.PROCEDURE_DESCRIPTION,
  [FormField.CONDITION_DIAGNOSIS]: FormFieldLabel.CONDITION_DIAGNOSIS,
  [FormField.PURPOSE]: FormFieldLabel.PURPOSE,
  [FormField.MATERIAL_RISK_A]: FormFieldLabel.MATERIAL_RISK_A,
  [FormField.MATERIAL_RISK_B]: FormFieldLabel.MATERIAL_RISK_B,
  [FormField.MATERIAL_RISK_C]: FormFieldLabel.MATERIAL_RISK_C,
  [FormField.MATERIAL_RISK_D]: FormFieldLabel.MATERIAL_RISK_D,
  [FormField.LIKELIHOOD_OF_SUCCESS]: FormFieldLabel.LIKELIHOOD_OF_SUCCESS,
  [FormField.PROGNOSIS_IF_DECLINED]: FormFieldLabel.PROGNOSIS_IF_DECLINED,
  [FormField.PRACTICAL_ALTERNATIVES]: FormFieldLabel.PRACTICAL_ALTERNATIVES,
  [FormField.INTERPRETER_NAMES]: FormFieldLabel.INTERPRETER_NAMES,
  [FormField.INTERPRETER_MODALITY]: FormFieldLabel.INTERPRETER_MODALITY,
  [FormField.LANGUAGE]: FormFieldLabel.LANGUAGE,
  [FormField.DISABILITY_ACCOMMODATIONS]: FormFieldLabel.DISABILITY_ACCOMMODATIONS,
  [FormField.DISABILITY_OTHER]: FormFieldLabel.DISABILITY_OTHER,
  [FormField.PATIENT_NAME]: FormFieldLabel.PATIENT_NAME,
  [FormField.PATIENT_SIGNATURE]: FormFieldLabel.PATIENT_SIGNATURE,
  [FormField.WITNESS_SIGNATURE]: FormFieldLabel.WITNESS_SIGNATURE,
  [FormField.PHYSICIAN_SIGNATURE]: FormFieldLabel.PHYSICIAN_SIGNATURE,
  [FormField.GUARDIAN_NAME]: FormFieldLabel.GUARDIAN_NAME,
  [FormField.ADDITIONAL_COMMENTS]: FormFieldLabel.ADDITIONAL_COMMENTS
};

export const FIELD_TYPE_MAP: Record<string, FormFieldInputType> = {
  [FormField.PROCEDURE_DESCRIPTION]: FormFieldInputType.TEXT_AREA,
  [FormField.CONDITION_DIAGNOSIS]: FormFieldInputType.TEXT_AREA,
  [FormField.PURPOSE]: FormFieldInputType.TEXT_AREA,
  [FormField.MATERIAL_RISK_A]: FormFieldInputType.TEXT_BOX,
  [FormField.MATERIAL_RISK_B]: FormFieldInputType.TEXT_BOX,
  [FormField.MATERIAL_RISK_C]: FormFieldInputType.TEXT_BOX,
  [FormField.MATERIAL_RISK_D]: FormFieldInputType.TEXT_BOX,
  [FormField.LIKELIHOOD_OF_SUCCESS]: FormFieldInputType.DROPDOWN,
  [FormField.PROGNOSIS_IF_DECLINED]: FormFieldInputType.TEXT_AREA,
  [FormField.PRACTICAL_ALTERNATIVES]: FormFieldInputType.TEXT_AREA,
  [FormField.INTERPRETER_NAMES]: FormFieldInputType.TEXT_BOX,
  [FormField.INTERPRETER_MODALITY]: FormFieldInputType.DROPDOWN,
  [FormField.LANGUAGE]: FormFieldInputType.DROPDOWN,
  [FormField.DISABILITY_ACCOMMODATIONS]: FormFieldInputType.MULTISELECT_DROPDOWN,
  [FormField.DISABILITY_OTHER]: FormFieldInputType.TEXT_BOX,
  [FormField.PATIENT_NAME]: FormFieldInputType.TEXT_BOX,
  [FormField.PATIENT_SIGNATURE]: FormFieldInputType.TEXT_BOX,
  [FormField.WITNESS_SIGNATURE]: FormFieldInputType.TEXT_BOX,
  [FormField.PHYSICIAN_SIGNATURE]: FormFieldInputType.TEXT_BOX,
  [FormField.GUARDIAN_NAME]: FormFieldInputType.TEXT_BOX,
  [FormField.ADDITIONAL_COMMENTS]: FormFieldInputType.TEXT_AREA
};

export const STATIC_LEGAL_TEXT = {
  ITEM_4_INTRO:
    'I understand that the practice of medicine is not an exact science. Consequently, all medical procedures have some risk. I understand that the procedure(s) described above may carry a material risk of infection, allergic reaction, disfiguring scar, severe loss of blood, loss of function of any limb or organ, paralysis or partial paralysis, paraplegia or quadriplegia, brain damage, cardiac arrest or death. In addition to these material risks, there may be other possible risks as a result of this procedure, including but not limited to:',
  ITEM_5:
    'I acknowledge and understand that during the course of the procedure(s) described above, it may become appropriate to perform additional procedures that are unforeseen or not known to be needed at the time this consent is obtained. Therefore, I hereby consent to and authorize this hospital, its medical staff, and those other medical personnel selected by the hospital to make the decision concerning performance of such procedure(s) as they may deem reasonably necessary in the exercise of their professional judgment. This consent shall also extend to the treatment of all conditions that may arise during the course of such procedures including those conditions that may be unknown or unforeseen at the time this consent is obtained.',
  ITEM_9: 'I authorize the hospital to dispose of any severed tissue, organs, or body parts in accordance with the law and hospital policies.',
  ITEM_10:
    'I also consent to diagnostic studies, tests, anesthesia, x-ray examinations, and any other treatment or course of treatment relating to the diagnosis or procedures described herein. A separate informed consent document may be required before some of these procedure(s).',
  ITEM_11:
    'If a medical device is implanted in me, I authorize the hospital to give to the implant manufacturer my name, address, telephone number, and social security number for implant tracking purpose(s).',
  ITEM_12: 'I understand that the hospital is a teaching center, and I authorize the presence of students and other observers for educational purposes during my procedure(s).',
  ITEM_13_INTRO:
    'I authorize the taking and publication of photographs or films (including videotape and television monitoring) of the procedure(s), subject to the following conditions:',
  ITEM_13_BULLET_1: 'that said photographs or films be used only for purposes of education and research; and',
  ITEM_13_BULLET_2: 'that the name of neither myself nor my family be used to identify said photographs or films.',
  ITEM_14:
    'I understand that I have the right to ask questions and I hereby certify that I have been given the opportunity to ask questions and that any such questions have been answered or explained in a satisfactory manner.',
  ITEM_15: 'I understand that no guarantees, assurances, or promises have been made to me concerning the results of this procedure.',
  ITEM_16:
    'By signing this form, I acknowledge that I have read it carefully or have had it read or explained to me and that I understand this form and its contents, and I hereby voluntarily consent to and request the hospital, its medical staff and all other medical personnel who may otherwise be involved in my treatment to perform the procedures described or otherwise referred to herein.',
  GUARDIAN_HELPER: 'hereby represent and certify that I am the spouse/parent/legal guardian of the patient, evidenced by my signature above.',
  INTERPRETER_SECTION_TITLE: 'Interpreter and Translated Materials',
  INTERPRETER_MATERIALS_LINE: "Materials presented in the patient's preferred language to meet Section 1557 requirements",
  DISABILITY_SECTION_TITLE: 'Disability Accommodations',
  SIGNATURES_SECTION_TITLE: 'Signatures',
  PROCEDURE_SUBTITLE: 'Surgical and Diagnostic Procedures'
};
